<template>
    <div v-if="editable" class="uk-grid uk-grid-small">
        <div class="uk-form-icon uk-width-1-2">
            <open-icon glyph="calendar" />
            <input
                ref="start"
                :id="date_field_id(name)"
                :name="name"
                :value="start_date_value(this.real_format)"
                autocomplete="off"
                :class="{'uk-form-small': small, 'uk-form-large': large, 'uk-form-warning': (required && !value[name]), 'uk-form-danger': out_of_bounds}"
                class="uk-width-1-1"
                @keyup.stop="keyup"
                @change="set_date(name, $event)"
                @focus="show(name, $event)"
            />
        </div>
        <div class="uk-form-icon uk-width-1-2">
            <open-icon glyph="calendar" />
            <input
                ref="end"
                :id="date_field_id(endName)"
                :name="endName"
                :value="end_date_value(this.real_format)"
                autocomplete="off"
                :class="{'uk-form-small': small, 'uk-form-large': large, 'uk-form-warning': (required && !value[endName]), 'uk-form-danger': out_of_bounds}"
                class="uk-width-1-1"
                @keyup.stop="keyup"
                @change="set_date(endName, $event)"
                @focus="show(endName, $event)"
            />
        </div>
        <slot></slot>
    </div>
    <div v-else class="uk-form-controls-text" @click.prevent="click">
        {{ display_value }}
        <slot></slot>
    </div>
</template>

<script>
import OpenIcon from "./OpenIcon"

// eslint-disable-next-line
let start_datepicker
// eslint-disable-next-line
let end_datepicker

export default {
    components: {
        OpenIcon
    },
    mounted () {
        // eslint-disable-next-line
        start_datepicker = UIkit.datepicker(this.$refs.start, this.datepicker_options).on("update.uk.datepicker", () => {
            const date_as_moment = this.date_value(this.name) || this.$moment()
            this.year_month_change("start", date_as_moment)
        }).on("hide.uk.datepicker", () => {
            this.set_date("start", { target: this.$refs.start })
        })
        // eslint-disable-next-line
        end_datepicker = UIkit.datepicker(this.$refs.end, this.datepicker_options).on("update.uk.datepicker", () => {
            const date_as_moment = this.date_value(this.endName) || this.$moment()
            this.year_month_change("end", date_as_moment)
        }).on("hide.uk.datepicker", () => {
            this.set_date("end", { target: this.$refs.end })
        })
    },
    methods: {
        date_field_id (date_name) {
            if(!this.value) return `${date_name}-${(new Date()).getTime()}`
            return date_name + "-" + this.value.id
        },
        date_value (date_name, filter = null) {
            if(!this.value || !this.value[date_name]) return ""
            const value = this.legacy ? this.$moment(this.value[date_name]) : this.$moment(this.value[date_name], this.real_input_format)
            if(!filter) return value
            return this.legacy ? value.format(this.real_input_format) : value.format(filter)
        },
        start_date_value (filter = null) {
            return this.date_value(this.name, filter)
        },
        end_date_value (filter = null) {
            return this.date_value(this.endName, filter)
        },
        keyup (event) {
            if(this.inputOnEnter && event && (event.code === "Enter")) {
                if(document.activeElement && document.activeElement.blur) document.activeElement.blur()
                else this.set_date(event.target.name, event)
            }
        },
        set_date (date_name, event) {
            let date_string = event.target.value
            if(this.legacy) {
                if(this.input_format === "MM-DD-YYYY") {
                    const matches = date_string.match(/^(\d{1,2})[\s\-./]+(\d{2}|\d{4})$/)
                    if(matches) date_string = matches[1] + "-" + this.$root.applicationSettings.default_date_day + "-" + matches[2]
                    date_string = date_string.replace(/([\s\-./]+)(\d{2})$/, "$120$2")
                    date_string = date_string.replace(/^(\d{1,2})[\s\-./]+(\d{1,2})[\s\-./]+(\d{2}|\d{4})$/, "$3-$1-$2")
                }
                if(date_string !== this.date_value(date_name, true)) this.date_picked(this.$moment(date_string))
            } else {
                if(date_string !== this.date_value(date_name, this.real_input_format)) this.date_picked(this.$moment(date_string, this.real_format))
            }
        },
        date_picked (picked_date) {
            const data = {}
            const start = this.date_value(this.name)
            const end = this.date_value(this.endName)

            if(!this.value[this.name] && this.value[this.endName]) {
                if(picked_date.isBefore(end)) data[this.name] = picked_date
                else {
                    data[this.name] = this.$moment(data[this.endName])
                    data[this.endName] = picked_date
                }
            } else if(this.value[this.name] && !this.value[this.endName]) {
                if(picked_date.isAfter(start)) data[this.endName] = picked_date
                else {
                    data[this.endName] = this.$moment(data[this.name])
                    data[this.name] = picked_date
                }
            } else if(picked_date.isBefore(start)) data[this.name] = picked_date
            else if(picked_date.isAfter(end)) data[this.endName] = picked_date
            else data[this.shown] = picked_date

            const formatted_data = {}
            Object.keys(data).forEach(field => {
                this.value[field] = data[field].format(this.real_input_format)
                formatted_data[field] = this.value[field]
            })
            this.$emit("change", formatted_data)
            this.range_highlight()
        },
        year_month_change (prefix, date_as_moment) {
            this[prefix + "_year"] = date_as_moment.year()
            this[prefix + "_month"] = date_as_moment.month()
            this.range_highlight()
        },
        show (date_name, event) {
            if(this.shown && (this.shown === date_name)) return
            event.target.select()

            this.shown = date_name

            const date_as_moment = this.date_value(date_name) || this.$moment()
            if(date_name === this.name) this.year_month_change("start", date_as_moment)
            else this.year_month_change("end", date_as_moment)
        },
        hide () {
            this.shown = false
            this.start_year = undefined
            this.start_month = undefined
            this.end_year = undefined
            this.end_month = undefined
        },
        range_highlight () {
            // any way to higlight dates within range?!?!
            setTimeout(() => {
                const start = this.date_value(this.name)
                const end = this.date_value(this.endName)

                const calendar = window.jQuery(".uk-dropdown.uk-datepicker")
                if(calendar && calendar.length) {
                    const cells = calendar.find("tbody td")
                    // We used to look at the "muted" cell class, but that doesn't work:
                    // when the clendar has a min/max, dates outside the range are also muted,
                    // causing our code to wronlgy see them as a different month, or week-end days...
                    cells.each(index => {
                        const cell = window.jQuery(cells.get(index))
                        const day_link = cell.children("a").removeClass("lam-date-range-start lam-date-range-date lam-date-range-end lam-date-week-end")
                        const cell_date = this.$moment(day_link.data("date"))

                        if(cell_date.isoWeekday() >= 6) day_link.addClass("lam-date-week-end")

                        if(cell_date.isAfter(start, "day") && cell_date.isBefore(end, "day")) day_link.addClass("lam-date-range-date")
                        else {
                            if(cell_date.isSame(start, "day")) day_link.addClass("lam-date-range-start")
                            if(cell_date.isSame(end, "day")) day_link.addClass("lam-date-range-end")
                        }
                        // console.log(cell_date.format("YYYY-MM-DD"), day_link.get(0))
                    })
                }
            }, 200)
        },
        click () {
            this.$emit("click", this.name, this.endName, this.value)
        }
    },
    computed: {
        datepicker_options () {
            return {
                offsettop: 0,
                // minDate: this.minDate,
                // maxDate: this.maxDate,
                format: this.legacy ? this.real_input_form : this.real_format,
                pos: this.calendarPosition
            }
        },
        display_value () {
            if(!this.value || (!this.value[this.name] && !this.value[this.endName])) return "(Never)"
            const parts = []
            if(this.value[this.name]) parts.push(this.$moment(this.value[this.name]).format(this.real_format))
            if(this.value[this.endName]) parts.push(this.$moment(this.value[this.endName]).format(this.real_format))
            return parts.join(" - ")
        },
        out_of_bounds () {
            const starts_at = this.start_date_value()
            if(starts_at) starts_at.startOf("day")
            const ends_at = this.end_date_value()
            if(ends_at) ends_at.endOf("day")
            if(this.minDate && starts_at && starts_at.isBefore(this.$moment(this.minDate).startOf("day"))) return true
            if(this.maxDate && ends_at && ends_at.isAfter(this.$moment(this.maxDate).endOf("day"))) return true
            return false
        },
        real_format () {
            if(this.legacy) return "MMMM DD, YYYY"
            return this.format
        },
        real_input_format () {
            if(this.legacy) return "MM-DD-YYYY"
            return this.input_format
        }
    },
    props: {
        id: {
            type: String,
            required: true
        },
        name: {
            type: String,
            required: true
        },
        endName: {
            type: String,
            required: true
        },
        value: {
            default: null
        },
        editable: {
            type: Boolean,
            default: true
        },
        required: {
            type: Boolean,
            default: false
        },
        minDate: {
            default: false
        },
        maxDate: {
            default: false
        },
        format: {
            type: String,
            default: "MM-DD-YYYY"
        },
        calendarPosition: {
            type: String,
            default: "auto"
        },
        inputOnEnter: {
            type: Boolean,
            default: false
        },
        small: {
            type: Boolean,
            default: false
        },
        large: {
            type: Boolean,
            default: false
        },
        legacy: { // format hard-coded to "MMMM DD, YYYY" and input_format hard-coded to "MM-DD-YYYY"
            type: Boolean,
            default: true
        }
    },
    data () {
        return {
            input_format: "YYYY-MM-DD",
            shown: false,
            start_year: undefined,
            start_month: undefined,
            end_year: undefined,
            end_month: undefined
        }
    }
}
</script>
